/**
 * AnexosBloco — bloco de anexos do orcamento (Bloco F item 4).
 *
 * Visual fiel ao mockup Fase 2: grid de cards (.attach-grid/.attach) com
 * icone por tipo de arquivo + nome + tamanho. Upload (toggle "Adicionar")
 * e delete preservados.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  FiAlertCircle,
  FiFile,
  FiFileText,
  FiGrid,
  FiImage,
  FiPlus,
  FiUpload,
} from "react-icons/fi";

import { useAuth } from "../../context/AuthContext";
import {
  deletarAnexo,
  listarAnexos,
  uploadAnexo,
} from "../../services/orcamentoAnexos";

import {
  EmptyMsg,
  ErrorMsg,
  Field,
  InlineHint,
  Input,
  PrimaryButton,
} from "./styles";

const MAX_BYTES = 10 * 1024 * 1024;

function fmtBytes(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

// Icone + classe de cor por extensao (mesmo mapeamento do mockup: pdf/img/xls).
function iconeAnexo(nome) {
  const ext = (nome || "").split(".").pop().toLowerCase();
  if (ext === "pdf") return { cls: "pdf", Icon: FiFileText };
  if (["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"].includes(ext)) {
    return { cls: "img", Icon: FiImage };
  }
  if (["xls", "xlsx", "csv"].includes(ext)) return { cls: "xls", Icon: FiGrid };
  return { cls: "", Icon: FiFile };
}

function AnexosBloco({ orcamentoId, editavel }) {
  const { request, hasAllPermissions, isMaster } = useAuth();
  const [anexos, setAnexos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [uploadando, setUploadando] = useState(false);
  const [arquivo, setArquivo] = useState(null);
  const [descricao, setDescricao] = useState("");
  const [mostrarUpload, setMostrarUpload] = useState(false);
  const fileInputRef = useRef(null);

  const podeEditar = editavel && (isMaster || hasAllPermissions(["orcamento:edit"]));

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const payload = await listarAnexos(request, orcamentoId);
      const items = Array.isArray(payload) ? payload : payload?.results || [];
      setAnexos(items);
      setErro(null);
    } catch (err) {
      setErro(err?.message || "Erro ao carregar anexos.");
    } finally {
      setLoading(false);
    }
  }, [request, orcamentoId]);

  useEffect(() => {
    if (orcamentoId) carregar();
  }, [orcamentoId, carregar]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!arquivo) {
      setErro("Selecione um arquivo.");
      return;
    }
    if (arquivo.size > MAX_BYTES) {
      setErro("Arquivo maior que 10MB.");
      return;
    }
    setUploadando(true);
    setErro(null);
    try {
      await uploadAnexo(request, { orcamentoId, arquivo, descricao });
      setArquivo(null);
      setDescricao("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      setMostrarUpload(false);
      await carregar();
    } catch (err) {
      setErro(err?.message || "Erro no upload.");
    } finally {
      setUploadando(false);
    }
  };

  const handleDelete = async (id, nome) => {
    if (!window.confirm(`Apagar anexo "${nome}"?`)) return;
    setErro(null);
    try {
      await deletarAnexo(request, id);
      await carregar();
    } catch (err) {
      setErro(err?.message || "Erro ao apagar.");
    }
  };

  return (
    <div className="block">
      <div className="block__head">
        <div className="block__title">
          Anexos <span style={{ color: "var(--ink-3)", fontWeight: 400 }}>· {anexos.length}</span>
        </div>
        {podeEditar && (
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => setMostrarUpload((v) => !v)}>
            <FiPlus size={12} /> Adicionar
          </button>
        )}
      </div>
      <div className="block__body">
        {loading ? (
          <EmptyMsg style={{ padding: 8 }}>Carregando...</EmptyMsg>
        ) : anexos.length === 0 ? (
          <EmptyMsg style={{ padding: 8 }}>Nenhum anexo.</EmptyMsg>
        ) : (
          <div className="attach-grid">
            {anexos.map((a) => {
              const { cls, Icon } = iconeAnexo(a.nome_original);
              return (
                <a
                  key={a.id}
                  className="attach"
                  href={a.arquivo_url}
                  target="_blank"
                  rel="noreferrer"
                  title={`Baixar ${a.nome_original} (${fmtBytes(a.tamanho_bytes)})${a.descricao ? ` — ${a.descricao}` : ""}`}
                >
                  <div className={`ic ${cls}`}><Icon size={14} /></div>
                  <div className="nm">{a.nome_original}</div>
                  <div className="sz">{fmtBytes(a.tamanho_bytes)}</div>
                  {podeEditar && (
                    <button
                      type="button"
                      className="attach__del"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDelete(a.id, a.nome_original); }}
                    >
                      Apagar
                    </button>
                  )}
                </a>
              );
            })}
          </div>
        )}

        {erro && (
          <ErrorMsg style={{ marginTop: 8 }}>
            <FiAlertCircle size={12} /> {erro}
          </ErrorMsg>
        )}

        {podeEditar && mostrarUpload && (
          <form
            onSubmit={handleUpload}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 6,
              marginTop: 12,
              paddingTop: 12,
              borderTop: "1px solid var(--divider)",
            }}
          >
            <Field>
              Anexar novo
              <Input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.dwg,.skp"
                onChange={(e) => setArquivo(e.target.files?.[0] || null)}
                disabled={uploadando}
              />
              {arquivo && (
                <InlineHint>
                  {arquivo.name} ({fmtBytes(arquivo.size)})
                </InlineHint>
              )}
            </Field>
            <Field>
              Descricao (opcional)
              <Input
                type="text"
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                placeholder="Ex: Contrato assinado"
                maxLength={200}
                disabled={uploadando}
              />
            </Field>
            <PrimaryButton type="submit" disabled={uploadando || !arquivo} style={{ alignSelf: "flex-start" }}>
              <FiUpload size={12} />
              {uploadando ? "Enviando..." : "Anexar"}
            </PrimaryButton>
            <InlineHint>Max 10MB. Tipos: PDF, imagem, Office, DWG, SKP.</InlineHint>
          </form>
        )}
      </div>
    </div>
  );
}

export default AnexosBloco;
